import { AgentMessageData } from '@/models/agentMessage';
import { AgentMessageType } from '@/models/enums';
import { TypeGuards, DataSanitizer } from './agentMessageValidator';

/**
 * Safe property access utility for agent message data and related objects
 * Provides type-safe property access with fallbacks and validation
 */
export class SafePropertyAccess {
  /**
   * Safely access nested properties with optional chaining simulation
   */
  static get<T>(obj: any, path: string, defaultValue: T): T {
    if (!obj || typeof obj !== 'object') {
      return defaultValue;
    }

    const keys = path.split('.');
    let current = obj;

    for (const key of keys) {
      if (current == null || typeof current !== 'object' || !(key in current)) {
        return defaultValue;
      }
      current = current[key];
    }

    return current !== undefined && current !== null ? current : defaultValue;
  }

  /**
   * Safely access agent name with fallback
   */
  static getAgentName(obj: any): string {
    const name = this.get(obj, 'agent', '') || 
                 this.get(obj, 'name', '') || 
                 this.get(obj, 'agent_name', '');
    return DataSanitizer.sanitizeAgentName(name);
  }

  /**
   * Safely access agent type with fallback and normalization
   */
  static getAgentType(obj: any): AgentMessageType {
    const type = this.get(obj, 'agent_type', null) || this.get(obj, 'type', null);
    return DataSanitizer.sanitizeAgentType(type);
  }

  /**
   * Safely access content with sanitization
   */
  static getContent(obj: any): string {
    const content = this.get(obj, 'content', '');
    return DataSanitizer.sanitizeContent(content);
  }

  /**
   * Safely access timestamp with validation
   */
  static getTimestamp(obj: any): number {
    const timestamp = this.get(obj, 'timestamp', null);
    return DataSanitizer.sanitizeTimestamp(timestamp);
  }

  /**
   * Safely access steps array
   */
  static getSteps(obj: any): any[] {
    const steps = this.get(obj, 'steps', []);
    return DataSanitizer.sanitizeArray(steps);
  }

  /**
   * Safely access next_steps array
   */
  static getNextSteps(obj: any): any[] {
    const nextSteps = this.get(obj, 'next_steps', []);
    return DataSanitizer.sanitizeArray(nextSteps);
  }

  /**
   * Safely access raw_data
   */
  static getRawData(obj: any): string {
    const rawData = this.get(obj, 'raw_data', '');
    return DataSanitizer.sanitizeContent(rawData);
  }

  /**
   * Create a safe agent message data object from any input
   */
  static createSafeAgentMessage(obj: any): AgentMessageData {
    return {
      agent: this.getAgentName(obj),
      agent_type: this.getAgentType(obj),
      content: this.getContent(obj),
      timestamp: this.getTimestamp(obj),
      steps: this.getSteps(obj),
      next_steps: this.getNextSteps(obj),
      raw_data: this.getRawData(obj),
      extra_data: obj?.extra_data || undefined
    };
  }

  /**
   * Safely check if object has required agent message properties
   */
  static hasRequiredAgentProperties(obj: any): boolean {
    if (!obj || typeof obj !== 'object') {
      return false;
    }

    // Check for agent name (flexible property names - include agent_name from backend)
    const hasAgent = TypeGuards.isString(obj.agent) || 
                     TypeGuards.isString(obj.name) || 
                     TypeGuards.isString(obj.agent_name);
    
    // Check for agent type (flexible property names)
    const hasAgentType = obj.agent_type !== undefined || obj.type !== undefined;
    
    return hasAgent && hasAgentType;
  }

  /**
   * Safely access array properties with validation
   */
  static getArrayProperty<T>(obj: any, path: string, defaultValue: T[] = []): T[] {
    const value = this.get(obj, path, defaultValue);
    return TypeGuards.isArray(value) ? value : defaultValue;
  }

  /**
   * Safely access string properties with sanitization
   */
  static getStringProperty(obj: any, path: string, defaultValue: string = ''): string {
    const value = this.get(obj, path, defaultValue);
    return TypeGuards.isString(value) ? DataSanitizer.sanitizeContent(value) : defaultValue;
  }

  /**
   * Safely access number properties with validation
   */
  static getNumberProperty(obj: any, path: string, defaultValue: number = 0): number {
    const value = this.get(obj, path, defaultValue);
    return TypeGuards.isNumber(value) ? value : defaultValue;
  }

  /**
   * Safely access boolean properties
   */
  static getBooleanProperty(obj: any, path: string, defaultValue: boolean = false): boolean {
    const value = this.get(obj, path, defaultValue);
    return typeof value === 'boolean' ? value : defaultValue;
  }

  /**
   * Validate type compatibility before state mutations
   */
  static validateTypeCompatibility<T>(
    currentValue: T,
    newValue: any,
    validator: (value: any) => value is T
  ): { isCompatible: boolean; safeValue: T } {
    if (validator(newValue)) {
      return { isCompatible: true, safeValue: newValue };
    }
    
    return { isCompatible: false, safeValue: currentValue };
  }

  /**
   * Safe data conversion with input/output validation
   */
  static safeConvert<TInput, TOutput>(
    input: TInput,
    converter: (input: TInput) => TOutput,
    inputValidator: (input: any) => input is TInput,
    outputValidator: (output: any) => output is TOutput,
    fallback: TOutput
  ): { success: boolean; result: TOutput; error?: string } {
    try {
      // Validate input
      if (!inputValidator(input)) {
        return {
          success: false,
          result: fallback,
          error: 'Input validation failed'
        };
      }

      // Perform conversion
      const result = converter(input);

      // Validate output
      if (!outputValidator(result)) {
        return {
          success: false,
          result: fallback,
          error: 'Output validation failed'
        };
      }

      return { success: true, result };
    } catch (error) {
      return {
        success: false,
        result: fallback,
        error: error instanceof Error ? error.message : 'Unknown conversion error'
      };
    }
  }

  /**
   * Create safe accessor methods for an object
   */
  static createSafeAccessors<T extends Record<string, any>>(obj: T) {
    return {
      get: <K extends keyof T>(key: K, defaultValue: T[K]): T[K] => {
        return this.get(obj, key as string, defaultValue);
      },
      
      getString: (key: keyof T, defaultValue: string = ''): string => {
        return this.getStringProperty(obj, key as string, defaultValue);
      },
      
      getNumber: (key: keyof T, defaultValue: number = 0): number => {
        return this.getNumberProperty(obj, key as string, defaultValue);
      },
      
      getBoolean: (key: keyof T, defaultValue: boolean = false): boolean => {
        return this.getBooleanProperty(obj, key as string, defaultValue);
      },
      
      getArray: <U>(key: keyof T, defaultValue: U[] = []): U[] => {
        return this.getArrayProperty(obj, key as string, defaultValue);
      },
      
      has: (key: keyof T): boolean => {
        return obj != null && typeof obj === 'object' && key in obj && obj[key] != null;
      },
      
      hasString: (key: keyof T): boolean => {
        const accessor = SafePropertyAccess.createSafeAccessors(obj);
        return accessor.has(key) && TypeGuards.isString(obj[key]);
      },
      
      hasNumber: (key: keyof T): boolean => {
        const accessor = SafePropertyAccess.createSafeAccessors(obj);
        return accessor.has(key) && TypeGuards.isNumber(obj[key]);
      },
      
      hasArray: (key: keyof T): boolean => {
        const accessor = SafePropertyAccess.createSafeAccessors(obj);
        return accessor.has(key) && TypeGuards.isArray(obj[key]);
      }
    };
  }
}

/**
 * Convenience functions for common safe property access patterns
 */

/**
 * Safe optional chaining for nested object access
 */
export const safeGet = <T>(obj: any, path: string, defaultValue: T): T => {
  return SafePropertyAccess.get(obj, path, defaultValue);
};

/**
 * Safe agent message property access
 */
export const safeAgentAccess = {
  name: (obj: any): string => SafePropertyAccess.getAgentName(obj),
  type: (obj: any): AgentMessageType => SafePropertyAccess.getAgentType(obj),
  content: (obj: any): string => SafePropertyAccess.getContent(obj),
  timestamp: (obj: any): number => SafePropertyAccess.getTimestamp(obj),
  steps: (obj: any): any[] => SafePropertyAccess.getSteps(obj),
  nextSteps: (obj: any): any[] => SafePropertyAccess.getNextSteps(obj),
  rawData: (obj: any): string => SafePropertyAccess.getRawData(obj)
};

/**
 * Type-safe property access with validation
 */
export const typeSafeAccess = {
  string: (obj: any, path: string, defaultValue: string = ''): string => {
    return SafePropertyAccess.getStringProperty(obj, path, defaultValue);
  },
  
  number: (obj: any, path: string, defaultValue: number = 0): number => {
    return SafePropertyAccess.getNumberProperty(obj, path, defaultValue);
  },
  
  boolean: (obj: any, path: string, defaultValue: boolean = false): boolean => {
    return SafePropertyAccess.getBooleanProperty(obj, path, defaultValue);
  },
  
  array: <T>(obj: any, path: string, defaultValue: T[] = []): T[] => {
    return SafePropertyAccess.getArrayProperty(obj, path, defaultValue);
  }
};

/**
 * Safe state mutation helpers
 */
export const safeMutation = {
  /**
   * Safely update state with type validation
   */
  updateState: <T>(
    currentState: T,
    updates: Partial<T>,
    validator?: (state: T) => boolean
  ): T => {
    try {
      const newState = { ...currentState, ...updates };
      
      if (validator && !validator(newState)) {
        console.warn('[SafeMutation] State validation failed, keeping current state');
        return currentState;
      }
      
      return newState;
    } catch (error) {
      console.error('[SafeMutation] State update failed:', error);
      return currentState;
    }
  },

  /**
   * Safely merge arrays with validation
   */
  mergeArrays: <T>(
    currentArray: T[],
    newItems: T[],
    validator?: (item: T) => boolean
  ): T[] => {
    try {
      if (!TypeGuards.isArray(currentArray)) {
        currentArray = [];
      }
      
      if (!TypeGuards.isArray(newItems)) {
        return currentArray;
      }
      
      const validItems = validator ? newItems.filter(validator) : newItems;
      return [...currentArray, ...validItems];
    } catch (error) {
      console.error('[SafeMutation] Array merge failed:', error);
      return currentArray;
    }
  }
};

export default SafePropertyAccess;